'use client'

import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  type ReactNode,
} from 'react'

type ArtifactCanvasHeaderSlotContextValue = {
  setHeaderActions: (actions: ReactNode) => void
  setTitle: (title: ReactNode) => void
  setTitleAddon: (addon: ReactNode) => void
}

const ArtifactCanvasHeaderSlotContext =
  createContext<ArtifactCanvasHeaderSlotContextValue | null>(null)

export function ArtifactCanvasHeaderSlotProvider({
  children,
  onHeaderActionsChange,
  onTitleChange,
  onTitleAddonChange,
}: {
  children: ReactNode
  onHeaderActionsChange: (actions: ReactNode) => void
  onTitleChange: (title: ReactNode) => void
  onTitleAddonChange: (addon: ReactNode) => void
}) {
  const setHeaderActions = useCallback(
    (actions: ReactNode) => {
      onHeaderActionsChange(actions)
    },
    [onHeaderActionsChange],
  )

  const setTitle = useCallback(
    (title: ReactNode) => {
      onTitleChange(title)
    },
    [onTitleChange],
  )

  const setTitleAddon = useCallback(
    (addon: ReactNode) => {
      onTitleAddonChange(addon)
    },
    [onTitleAddonChange],
  )

  const value = useMemo(
    () => ({ setHeaderActions, setTitle, setTitleAddon }),
    [setHeaderActions, setTitle, setTitleAddon],
  )

  return (
    <ArtifactCanvasHeaderSlotContext.Provider value={value}>
      {children}
    </ArtifactCanvasHeaderSlotContext.Provider>
  )
}

/** Register trailing header controls from `RichTextPlateShell` when `modeSwitcher="header"`. */
export function useArtifactCanvasHeaderActions(actions: ReactNode) {
  const setHeaderActions = useContext(ArtifactCanvasHeaderSlotContext)?.setHeaderActions

  useLayoutEffect(() => {
    if (!setHeaderActions) return
    setHeaderActions(actions)
    return () => {
      setHeaderActions(null)
    }
  }, [actions, setHeaderActions])
}

/**
 * Override the shell header title from canvas content (e.g. after document hydrate).
 * Clears on unmount so the open-time titleHint returns as fallback.
 */
export function useArtifactCanvasHeaderTitle(title: ReactNode) {
  const setTitle = useContext(ArtifactCanvasHeaderSlotContext)?.setTitle

  useLayoutEffect(() => {
    if (!setTitle) return
    setTitle(title)
    return () => {
      setTitle(null)
    }
  }, [title, setTitle])
}

/** Register inline controls beside the canvas header title (e.g. pin). */
export function useArtifactCanvasHeaderTitleAddon(addon: ReactNode) {
  const setTitleAddon = useContext(ArtifactCanvasHeaderSlotContext)?.setTitleAddon

  useLayoutEffect(() => {
    if (!setTitleAddon) return
    setTitleAddon(addon)
    return () => {
      setTitleAddon(null)
    }
  }, [addon, setTitleAddon])
}
